import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.threshold');
import { OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Service } from "src/libs/portal/season/service";
import {
    cellyticsAnalysisSet,
    cellyticsCurrentAnalysisSetId,
    cellyticsDefaultAnalysisSet,
} from '../cellytics-analysis';

const ACCESS_SESSION_KEY = "cellytics.access";
const ACCESS_SESSION_VALUE = "granted";
const ACCESS_USER = {
    id: "cellytics-access",
    email: "",
    name: "Cellytics NK",
    role: "admin",
};
const THRESHOLD_ANALYSIS_URL = "/assets/cellytics/260622/threshold-analysis-summary.json";
const THRESHOLD_SUBJECT_ROWS_URL = "/assets/cellytics/260622/threshold-subject-rows.json";
const DELETION_EXAMPLES_URL = "/assets/cellytics/260622/deletion-examples.json";

@Component({
    selector: 'wiz-page-threshold',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.threshold/view.scss */
/* src/app/page.threshold/view.scss: no such file or directory */`],
})
export class PageThresholdComponent implements OnInit, OnDestroy {
    public data: any = null;
    public deletionExamplesData: any = null;
    public subjectRowsData: any = null;
    public analysisSetId: string = "";
    public analysisSet: any = null;
    public loading: boolean = true;
    public subjectRowsLoading: boolean = false;
    public error: string = "";
    public selectedCaseId: string = "mmd_filter";
    public selectedScopeId: string = "combined";
    public selectedThreshold: number = 13.5;

    private routerSub: any = null;
    private loadToken: number = 0;
    private subjectRowsPromise: Promise<any> | null = null;

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    public async ngOnInit() {
        await this.service.init();
        if (!this.hasAccess()) {
            location.href = "/access";
            return;
        }
        this.applyAccessSession();
        this.syncAnalysisSet();
        this.bindRouteChanges();
        await this.load();
    }

    public ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
    }

    private hasAccess() {
        return this.service.auth.check() || window.sessionStorage.getItem(ACCESS_SESSION_KEY) === ACCESS_SESSION_VALUE;
    }

    private applyAccessSession() {
        if (this.service.auth.check()) return;
        this.service.auth.status = true;
        this.service.auth.session = ACCESS_USER;
    }

    private syncAnalysisSet() {
        this.analysisSetId = cellyticsCurrentAnalysisSetId();
        this.analysisSet = cellyticsAnalysisSet(this.analysisSetId) || cellyticsDefaultAnalysisSet();
    }

    private bindRouteChanges() {
        if (this.routerSub) return;
        this.routerSub = this.router.events.subscribe(async (event: any) => {
            if (!(event instanceof NavigationEnd)) return;
            const nextSetId = cellyticsCurrentAnalysisSetId();
            if (nextSetId === this.analysisSetId) return;
            this.syncAnalysisSet();
            await this.load();
        });
    }

    public async load() {
        const token = ++this.loadToken;
        this.syncAnalysisSet();
        this.loading = true;
        this.subjectRowsLoading = false;
        this.error = "";
        this.deletionExamplesData = null;
        this.subjectRowsData = null;
        this.subjectRowsPromise = null;
        await this.service.render();

        try {
            const response = await fetch(THRESHOLD_ANALYSIS_URL);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this.data = await response.json();
            this.selectedCaseId = this.caseOptions[0]?.caseId || "mmd_filter";
            this.selectedScopeId = "combined";
            this.selectedThreshold = this.selectedCaseAnalysis?.referenceThreshold || this.selectedCaseAnalysis?.bestThreshold || 13.5;
        } catch (error) {
            this.error = "threshold 분석 데이터를 불러오지 못했습니다.";
        }

        this.loading = false;
        await this.service.render();
        if (!this.error && token === this.loadToken) this.loadDeferredData(token);
    }

    private loadDeferredData(token: number) {
        this.loadDeletionExamples(token);
        this.ensureSubjectRowsLoaded(token);
    }

    private async loadDeletionExamples(token: number) {
        try {
            const examplesResponse = await fetch(DELETION_EXAMPLES_URL);
            if (token !== this.loadToken) return;
            this.deletionExamplesData = examplesResponse.ok ? await examplesResponse.json() : null;
        } catch (error) {
            if (token !== this.loadToken) return;
            this.deletionExamplesData = null;
        }
        if (token === this.loadToken) await this.service.render();
    }

    private async ensureSubjectRowsLoaded(token: number = this.loadToken) {
        if (this.subjectRowsData) return this.subjectRowsData;
        if (!this.subjectRowsPromise) {
            this.subjectRowsLoading = true;
            await this.service.render();
            this.subjectRowsPromise = fetch(THRESHOLD_SUBJECT_ROWS_URL)
                .then((response: any) => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return response.json();
                });
        }
        try {
            const data = await this.subjectRowsPromise;
            if (token !== this.loadToken) return null;
            this.subjectRowsData = data;
        } catch (error) {
            if (token !== this.loadToken) return null;
            this.subjectRowsData = { scopes: {} };
        }
        this.subjectRowsLoading = false;
        if (token === this.loadToken) await this.service.render();
        return this.subjectRowsData;
    }

    public get is260622() {
        return this.analysisSetId === "260622";
    }

    public get caseOptions() {
        return this.data?.combined?.caseSummaries || [];
    }

    public get datasets() {
        return this.data?.datasets || [];
    }

    public get scopeOptions() {
        return [
            { id: "combined", label: "통합" },
            ...this.datasets.map((row: any) => ({ id: row.datasetId, label: row.label })),
        ];
    }

    public get selectedScope() {
        if (this.selectedScopeId === "combined") return this.data?.combined || null;
        return this.datasets.find((row: any) => row.datasetId === this.selectedScopeId) || this.data?.combined || null;
    }

    public get selectedCaseAnalysis() {
        return this.selectedScope?.cases?.find((row: any) => row.caseId === this.selectedCaseId)
            || this.data?.combined?.cases?.find((row: any) => row.caseId === this.selectedCaseId)
            || null;
    }

    public get selectedThresholdRow() {
        const rows = this.selectedCaseAnalysis?.summary || [];
        return rows.find((row: any) => Number(row.threshold) === Number(this.selectedThreshold))
            || this.selectedCaseAnalysis?.bestThresholdRow
            || rows[0]
            || null;
    }

    public get thresholdRows() {
        return this.selectedCaseAnalysis?.summary || [];
    }

    public get subjectRows() {
        const thresholdKey = this.thresholdDetailKey(this.selectedThresholdRow);
        return this.subjectRowsData?.scopes?.[this.selectedScopeId]?.[this.selectedCaseId]?.[thresholdKey]
            || this.selectedThresholdRow?.subjectRows
            || [];
    }

    public get referenceCells() {
        return this.data?.referenceCells || [];
    }

    public get deletionExamples() {
        return this.deletionExamplesData?.cases?.[this.selectedCaseId] || [];
    }

    public get datasetComparisonRows() {
        return this.datasets
            .map((dataset: any) => {
                const row = dataset.caseSummaries?.find((item: any) => item.caseId === this.selectedCaseId);
                if (!row) return null;
                return {
                    datasetId: dataset.datasetId,
                    datasetLabel: dataset.label,
                    ...row,
                };
            })
            .filter((row: any) => !!row && !!row.caseId);
    }

    public async selectCase(caseId: string) {
        this.selectedCaseId = caseId;
        this.selectedThreshold = this.selectedCaseAnalysis?.referenceThreshold || this.selectedCaseAnalysis?.bestThreshold || this.selectedThreshold;
        await this.service.render();
        this.ensureSubjectRowsLoaded();
    }

    public async selectScope(scopeId: string) {
        this.selectedScopeId = scopeId;
        const available = this.selectedScope?.caseSummaries?.some((row: any) => row.caseId === this.selectedCaseId);
        if (!available) this.selectedCaseId = this.selectedScope?.caseSummaries?.[0]?.caseId || this.selectedCaseId;
        this.selectedThreshold = this.selectedCaseAnalysis?.referenceThreshold || this.selectedCaseAnalysis?.bestThreshold || this.selectedThreshold;
        await this.service.render();
        this.ensureSubjectRowsLoaded();
    }

    public async selectThreshold(value: any) {
        this.selectedThreshold = Number(value);
        await this.service.render();
        this.ensureSubjectRowsLoaded();
    }

    private thresholdDetailKey(row: any) {
        if (!row) return "";
        return String(row.thresholdLabel || row.threshold || "");
    }

    public caseActiveClass(caseId: string) {
        if (caseId === this.selectedCaseId) {
            return "rounded-lg border border-indigo-500 bg-indigo-50 px-3 py-2 text-left text-[12px] font-semibold text-indigo-800";
        }
        return "rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-[12px] font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50";
    }

    public scopeActiveClass(scopeId: string) {
        if (scopeId === this.selectedScopeId) {
            return "rounded-md bg-gray-950 px-3 py-1.5 text-[12px] font-semibold text-white";
        }
        return "rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-600 hover:bg-gray-50";
    }

    public rowActiveClass(value: any) {
        if (Number(value) === Number(this.selectedThreshold)) return "bg-indigo-50";
        return "hover:bg-gray-50";
    }

    public diagnosisClass(diagnosis: string) {
        if (diagnosis === "normal") return "rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700";
        if (diagnosis === "patient") return "rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700";
        return "rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500";
    }

    public deltaClass(value: any) {
        if (value > 0) return "text-blue-700";
        if (value < 0) return "text-red-600";
        return "text-gray-500";
    }

    public retentionClass(value: any) {
        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) return "text-gray-500";
        const target = Number(this.selectedCaseAnalysis?.normalRetentionTargetPercent || this.data?.parameters?.normalRetentionTargetPercent || 97);
        if (numberValue >= target) return "text-emerald-700";
        if (numberValue >= 95) return "text-amber-700";
        return "text-red-600";
    }

    public reductionClass(value: any) {
        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) return "text-gray-500";
        if (numberValue > 0) return "text-rose-700";
        if (numberValue < 0) return "text-blue-700";
        return "text-gray-500";
    }

    public referenceCellRemoved(cell: any) {
        const mmdSd = Number(cell?.metrics?.MMD_SD);
        if (Number.isNaN(mmdSd)) return false;
        return mmdSd >= Number(this.selectedThreshold);
    }

    public referenceCellStatus(cell: any) {
        return this.referenceCellRemoved(cell) ? "삭제" : "유지";
    }

    public referenceCellStatusClass(cell: any) {
        const removed = this.referenceCellRemoved(cell);
        if (cell?.diagnosis === "normal") {
            return removed
                ? "mt-1 rounded bg-red-50 px-1.5 py-0.5 text-center text-[10px] font-semibold text-red-700"
                : "mt-1 rounded bg-emerald-50 px-1.5 py-0.5 text-center text-[10px] font-semibold text-emerald-700";
        }
        return removed
            ? "mt-1 rounded bg-rose-50 px-1.5 py-0.5 text-center text-[10px] font-semibold text-rose-700"
            : "mt-1 rounded bg-amber-50 px-1.5 py-0.5 text-center text-[10px] font-semibold text-amber-700";
    }

    public signed(value: any, digits: number = 0) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
        const numberValue = Number(value);
        const prefix = numberValue > 0 ? "+" : "";
        return `${prefix}${this.format(numberValue, digits)}`;
    }

    public format(value: any, digits: number = 0) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
        return Number(value).toLocaleString("ko-KR", {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        });
    }

    public formatPercent(value: any, digits: number = 1) {
        const formatted = this.format(value, digits);
        if (formatted === "-") return formatted;
        return `${formatted}%`;
    }
}

export default PageThresholdComponent;